import { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Box, Grid, Typography } from '@mui/material';
import { format, parseISO, isBefore } from 'date-fns';
import LoopIcon from '@mui/icons-material/Loop';
import { getDateLocale } from '../../lib/dateLocale';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import {
    AppContext,
    SttDialog,
    SttTabs,
    SttFormDate,
    SttFormSelect,
    SttFormTextarea,
    SttSelectPForm,
    SttFeeList,
    SttLabelForm,
    SttSelectUser,
    SttFormCheck,
} from '../../sporttia/all';
import { useCrud, useInteractionsFiles, useLoader } from '../../lib/hooks';
import {
    generateFormField,
    fullName,
    formatPriceByLocale,
    composeFormData,
    getErrorMessage,
} from '../../lib/utils';
import HandlePaymentsModal from '../../pages/sc/groups/HandlePaymentsModal';
import { SttSelectClassAndRateDialog } from '../../sporttia/dialogs/SttSelectClassAndRateDialog';
import DataphonePaymentDialog from './DataphonePaymentDialog';
import POSStripePaymentDialog from '../stripe/POSStripePaymentDialog';
import CashdroControlDialog from './CashdroControlDialog';
import constants from '../../config/constants';
import { isRenewalPreenrollmentVisible } from '../../config/features';
import { isPreenrollmentWindowOpen } from '../../lib/preenrollment';
import useStudentsService from '../../services/StudentsService';
import useActivitiesService from '../../services/ActivitiesService';
import useActivityRatesService from '../../services/ActivityRatesService';
import DiscountList from '../discounts/DiscountsList';
import type { Activity } from '../../types/models/Activity';
import type { Student } from '../../types/models/Student';
import { PosPaymentParams } from '../../types/payment';
import type { Discount } from '../../types/models/Discount';
import { PERMISSIONS } from '../../lib/permissions';

type DiscountForm = Discount & {
    checked: boolean;
};

type HistoryItem = {
    id: number;
    ini: string;
    end: string;
};

type ExtendedUser = {
    id?: number;
    name?: string;
    surname1?: string;
    surname2?: string;
    fullName?: string;
    mship?: {
        tutor?: {
            name?: string;
            surname1?: string;
            surname2?: string;
        };
    };
};

type ExtendedStudent = Student & {
    user?: ExtendedUser | null;
    discounts?: DiscountForm[];
};

type ActivityFormQuestion = {
    id: number;
    name: string;
    type: string;
    typeLang: string;
    mandatory: boolean;
};

type ExtendedActivity = Activity & {
    form?: ActivityFormQuestion[];
};

type StudentDialogProps = {
    open?: boolean;
    idStudent?: number | null;
    defaultIdRate?: number | null;
    activity?: ExtendedActivity | null;
    onSave: () => void;
    onClose: () => void;
    trash?: boolean;
};

type FormFieldChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type PaymentFormOverride = {
    enrollmentPaymentForm?: string;
    feePaymentForm?: string;
};

/**
 * Student dialog
 */
export default function StudentDialog({
    open = false,
    idStudent = null,
    defaultIdRate = null,
    activity = null,
    onSave,
    onClose,
    trash,
}: StudentDialogProps): React.ReactElement | null {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const [student, setStudent, setProperty, Get, , , Delete] =
        useCrud<ExtendedStudent>({} as ExtendedStudent);
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [history, setHistory] = useState<HistoryItem[]>();
    const [idRate, setIdRate] = useState<number | null>(defaultIdRate);
    const [openAskingPaymentProcedure, setOpenAskingPaymentProcedure] =
        useState(false);
    const [openChangeClassAndRateDialog, setOpenChangeClassAndRateDialog] =
        useState(false);
    const [posPaymentParams, setPosPaymentParams] =
        useState<PosPaymentParams | null>(null);
    const [discountsForm, setDiscountsForm] = useState<DiscountForm[]>([]);
    const [cashdroPayment, setCashdroPayment] = useState(false);
    const [cashdroPaymentAmount, setCashdroPaymentAmount] = useState(0);
    const [pendingPaymentForm, setPendingPaymentForm] =
        useState<PaymentFormOverride | null>(null);
    const creating = idStudent === 0;

    const selectedRate =
        activity?.rates?.find((rate: any) => rate.id === idRate) ??
        student?.rate;

    function saveAndClose(): void {
        onSave();
        onClose();
    }

    function handleChangeDiscounts({
        name: id,
        value,
    }: FormFieldChangeEvent): void {
        setDiscountsForm((prev: any) =>
            prev.map((discount: any) =>
                discount.id === Number(id)
                    ? {
                          ...discount,
                          checked: Boolean(value),
                      }
                    : discount,
            ),
        );
    }

    const loadStudent = (): void => {
        Get(`/students/${idStudent}`, { trash })
            .then((response: unknown) => {
                if (
                    response &&
                    typeof response === 'object' &&
                    'student' in response
                ) {
                    const studentResponse = response as {
                        student: ExtendedStudent;
                    };
                    setStudent(studentResponse.student);
                    setIdRate(studentResponse.student.rate.id);
                }
            })
            .catch(() => {});
    };

    const loadHistory = (): void => {
        Get(`/students/${idStudent}/history`)
            .then((result: unknown) => {
                if (result && typeof result === 'object' && 'rows' in result) {
                    const historyResult = result as { rows: HistoryItem[] };
                    setHistory(historyResult.rows);
                }
            })
            .catch(() => {});
    };

    // Init. If an idStudent is provided load it, otherwise create an 'empty' student for addition
    useEffect(() => {
        if (!creating) {
            setStudent({} as ExtendedStudent);
            loadStudent();
            loadHistory();
            setTitle(t('generic.student'));
        } else {
            // Aquí activity debe existir siempre...
            if (!activity || !selectedRate) {
                return;
            }

            const now = new Date();
            const then = parseISO(selectedRate.ini);

            setStudent({
                name: '',
                user: null as unknown as ExtendedStudent['user'],
                ini: isBefore(now, then)
                    ? format(then, 'yyyy-MM-dd HH:mm:ss')
                    : format(now, 'yyyy-MM-dd 00:00:00'),
                end: format(parseISO(selectedRate.end), 'yyyy-MM-dd HH:mm:ss'),
                notes: '',
                rate: selectedRate as any,
                enrollment: {} as ExtendedStudent['enrollment'],
                feePaymentForm: '',
                form: activity.form
                    ? activity.form.map((question: any) => ({
                          id: question.id,
                          name: question.name,
                          type: question.type,
                          typeLang: question.typeLang,
                          mandatory: question.mandatory,
                          options: question.options,
                          answer: { id: 0, value: '' },
                      }))
                    : null,
            } as ExtendedStudent);

            setHistory(undefined);
        }
        // Eliminar useEffect cuando se migre a react-query
    }, [idStudent]);

    const studentsService = useStudentsService();
    const updateStudentMutation = studentsService.useUpdateStudent({
        onSuccess: () => {
            onSave();
        },
        onError: (error: unknown) => {
            cxt?.showMessage('E', getErrorMessage(error));
        },
    });

    const generateStudentEnrollmentMutation =
        studentsService.useGenerateStudentEnrollment({
            onSuccess: () => {
                cxt?.showMessage('S', t('GeneratedEnrollment'));
                loadStudent();
            },
            onError: (error: unknown) => {
                cxt?.showMessage('E', getErrorMessage(error));
            },
        });

    const activitiesService = useActivitiesService();
    const createActivityRateStudentMutation =
        activitiesService.useCreateActivityRateStudent({
            onSuccess: (response: unknown) => {
                if (
                    response &&
                    typeof response === 'object' &&
                    'tpv' in response
                ) {
                    const tpvResponse = response as {
                        tpv?: {
                            payment?: { paymentForm?: string };
                            price?: number;
                            concept?: string;
                            id?: number;
                            sc?: {
                                id?: number;
                                short?: string;
                                customer?: { name?: string; cif?: string };
                                address?: string;
                                phonePublic?: string;
                            };
                        };
                    };
                    const { tpv } = tpvResponse;

                    if (
                        tpv?.payment?.paymentForm ===
                        constants.payment.paymentForms.dataphoneConnected.name
                    ) {
                        // params for the POS dialog (datáfono físico)
                        setPosPaymentParams({
                            amount: tpv.price ?? 0,
                            concept: tpv.concept ?? '',
                            idTpv: tpv.id ?? 0,
                            sc: {
                                id: tpv?.sc?.id,
                                scName: tpv?.sc?.short,
                                entityName: tpv?.sc?.customer?.name,
                                address: tpv?.sc?.address,
                                cif: tpv?.sc?.customer?.cif,
                                phone: tpv?.sc?.phonePublic,
                            },
                            returnParams: undefined,
                        });
                    } else {
                        saveAndClose();
                    }
                } else {
                    // No TPV payment required, just save and close
                    saveAndClose();
                }
            },
            onError: (error: unknown) => {
                cxt?.showMessage('E', getErrorMessage(error));
            },
        });

    function handleStudentSave(
        paymentFormOverride: PaymentFormOverride | null = null,
    ): void {
        if (!student || !student.user) return;
        const CASHDRO = cxt?.sc?.cashdroConnected;
        const enrollmentForm =
            paymentFormOverride?.enrollmentPaymentForm ??
            student.enrollment?.paymentForm;
        const enrollmentAmount = selectedRate?.enrollment || 0;
        if (
            CASHDRO &&
            enrollmentForm === constants.payment.paymentForms.cashdro.name &&
            enrollmentAmount > 0
        ) {
            setPendingPaymentForm(paymentFormOverride);
            setCashdroPaymentAmount(enrollmentAmount);
            setCashdroPayment(true);
            return;
        }
        let requestBody: Record<string, unknown>;
        if (student.id) {
            const { ini, end, notes, feePaymentForm, enrollment, form } =
                student;
            requestBody = {
                notes,
                ini,
                end,
                feePaymentForm:
                    paymentFormOverride?.feePaymentForm ?? feePaymentForm,
                enrollmentPaymentForm:
                    paymentFormOverride?.enrollmentPaymentForm ??
                    (enrollment ? enrollment.paymentForm : null),
            };
            if (form) {
                requestBody.form = composeFormData(form as any);
            }
            updateStudentMutation.mutate({
                id: idStudent as number,
                params: requestBody as any,
            });
        } else {
            const { user, ini, end, notes, feePaymentForm, enrollment, form } =
                student;
            const discountsIdsToSend = discountsForm
                .filter((discount: any) => discount.checked)
                .map((discount: any) => discount.id);
            requestBody = {
                idUser: user?.id,
                notes,
                ini,
                end,
                feePaymentForm:
                    paymentFormOverride?.feePaymentForm ?? feePaymentForm,
                enrollmentPaymentForm:
                    paymentFormOverride?.enrollmentPaymentForm ??
                    enrollment?.paymentForm,
                discountsIds: discountsIdsToSend,
            };
            if (form) {
                requestBody.form = composeFormData(form as any);
            }
            createActivityRateStudentMutation.mutate({
                idRate: idRate as number,
                params: requestBody as any,
            });
        }
    }

    const upsertStudent = (): void => handleStudentSave();

    const activityRateService = useActivityRatesService();
    const activityRateDiscountsQuery =
        activityRateService.useGetActivityRateDiscounts(
            selectedRate?.id,
            {
                rows: 1000,
            },
            {
                refetchOnMount: true,
                enabled: creating,
            },
        );

    useEffect(() => {
        if (activityRateDiscountsQuery.data) {
            const response = activityRateDiscountsQuery.data;
            if (
                response &&
                typeof response === 'object' &&
                'rows' in response
            ) {
                setDiscountsForm(
                    response.rows.map((discount: any) => ({
                        ...discount,
                        checked: false,
                    })),
                );
            }
        }
    }, [activityRateDiscountsQuery.data]);

    const download = (): void => {
        downloadFile(
            `/students/${idStudent}.pdf`,
            'application/pdf',
            undefined,
            'student.pdf',
        );
    };

    const handleForm = ({ name, value }: FormFieldChangeEvent): void => {
        setStudent(
            (prev: any) =>
                ({ ...prev, [name as string]: value }) as ExtendedStudent,
        );
    };

    function handleFormFieldChange({
        name,
        value,
    }: FormFieldChangeEvent): void {
        const form = student?.form;
        if (!form) return;
        const foundQuestion = form.find(
            (question: any) => question.id.toString() === name,
        );
        if (!foundQuestion) return;
        form.splice(form.indexOf(foundQuestion), 1, {
            ...foundQuestion,
            answer: { value: value as string | boolean },
        });
        setStudent((prev: any) => ({ ...prev, form }));
    }

    function setEnrollmentPaymentForm(paymentForm: string): void {
        if (!student) return;
        setStudent({
            ...student,
            enrollment: {
                ...student.enrollment,
                paymentForm,
            },
        });
    }

    function cancelActivity(deletingPaymentProcedure?: number): void {
        let params: Record<string, unknown> | undefined;
        if (deletingPaymentProcedure !== undefined) {
            params = {
                deletingPaymentAction: deletingPaymentProcedure,
            };
        }
        Delete(`/students/${idStudent}`, params).then(() => {
            setOpenAskingPaymentProcedure(false);
            saveAndClose();
        });
    }

    const deleteInscription = (): void => {
        cxt?.api('DELETE', `/activities/students/${student.id}`, {
            confirmation: true,
            success: () => {
                cxt.showMessage('S', 'Deleted');
                saveAndClose();
            },
        });
    };

    const [isMutationsLoading, mutationsLoader] = useLoader([
        createActivityRateStudentMutation.status,
        updateStudentMutation.status,
        generateStudentEnrollmentMutation.status,
    ]);

    if (isMutationsLoading) {
        return mutationsLoader;
    }

    if (!student) {
        return null;
    }

    const hasTutor = student.user?.mship?.tutor;
    const discounts = creating ? discountsForm : student.discounts;

    const showDiscountsSelection =
        (selectedRate?.fee > 0 || selectedRate?.enrollment > 0) &&
        creating &&
        (discounts?.length ?? 0) > 0;

    const showDiscountsList =
        selectedRate?.fee > 0 && !creating && (discounts?.length ?? 0) > 0;

    // CU-869f643fv: staff may enroll anyone during pre-enrollment, so they are
    // reminded that the places are held for the students who renew
    const showPreenrollmentWarning =
        creating &&
        isRenewalPreenrollmentVisible() &&
        isPreenrollmentWindowOpen(activity, new Date());

    const tabStudent = (
        <Grid container spacing={3}>
            <>
                {showPreenrollmentWarning && (
                    <Grid size={{ xs: 12 }}>
                        <Alert severity="warning">
                            <AlertTitle>
                                {t('activity.preenrollment.staffWarning.title')}
                            </AlertTitle>
                            {t('activity.preenrollment.staffWarning.body')}
                        </Alert>
                    </Grid>
                )}
                {student.id ? (
                    <SttLabelForm
                        grid
                        xs={12}
                        sm={hasTutor ? 6 : 12}
                        md={hasTutor ? 3 : 6}
                        title={t('generic.name')}
                        text={String(
                            fullName(
                                student.user as Parameters<typeof fullName>[0],
                            ),
                        )}
                    />
                ) : (
                    <SttSelectUser
                        grid
                        xs={12}
                        md={6}
                        caption={t('generic.name')}
                        name="user"
                        user={student.user}
                        onSelect={(mship: unknown) => {
                            if (
                                mship &&
                                typeof mship === 'object' &&
                                'user' in mship
                            ) {
                                const mshipData = mship as {
                                    user: Record<string, unknown>;
                                };
                                setProperty({
                                    name: 'user',
                                    value: {
                                        ...mshipData.user,
                                        mship,
                                    } as any,
                                });
                            } else {
                                setProperty({
                                    name: 'user',
                                    value: undefined as any,
                                });
                            }
                        }}
                        returnMship
                    />
                )}

                {hasTutor && (
                    <SttLabelForm
                        grid
                        xs={12}
                        sm={6}
                        md={3}
                        title={t('Tutor')}
                        text={String(
                            fullName(
                                student.user?.mship?.tutor as Parameters<
                                    typeof fullName
                                >[0],
                            ),
                        )}
                    />
                )}

                <SttFormDate
                    grid
                    md={3}
                    sm={6}
                    xs={12}
                    name="ini"
                    caption={t('Begin')}
                    defVal={student.ini}
                    onChange={(value) => handleForm(value)}
                />

                <SttFormDate
                    grid
                    md={3}
                    sm={6}
                    xs={12}
                    name="end"
                    caption={t('End')}
                    defVal={student.end}
                    onChange={(value) => handleForm(value)}
                />

                <SttFormTextarea
                    grid
                    name="notes"
                    caption={t('generic.notes')}
                    defVal={student.notes}
                    onChange={(value) => handleForm(value)}
                />

                <SttFormSelect
                    grid
                    xs={12}
                    md={6}
                    name="rate"
                    caption={t('generic.rate')}
                    defVal={idRate}
                    disabled
                    options={(() => {
                        if (activity?.rates) {
                            return activity.rates.map((rate: any) => ({
                                caption: rate.name,
                                value: rate.id,
                            }));
                        }
                        if (student?.rate) {
                            return [
                                {
                                    caption: student.rate.name,
                                    value: student.rate.id,
                                },
                            ];
                        }
                        return [];
                    })()}
                    onChange={({ value }) => setIdRate(value as number)}
                />

                {/* --- Payment form - signup --- */}
                <SttSelectPForm
                    grid
                    xs={12}
                    md={3}
                    disabled={
                        !!student.id ||
                        (selectedRate && selectedRate.enrollment === 0)
                    }
                    name="enrollmentPaymentForm"
                    caption={`${t('generic.inscription')}${
                        selectedRate
                            ? `[ ${formatPriceByLocale(
                                  selectedRate.enrollment,
                                  typeof selectedRate.enrollmentShape ===
                                      'object'
                                      ? (selectedRate.enrollmentShape
                                            ?.currency ?? 'EUR')
                                      : 'EUR',
                                  typeof selectedRate.enrollmentShape ===
                                      'object'
                                      ? (selectedRate.enrollmentShape?.locale ??
                                            'es-ES')
                                      : 'es-ES',
                              )} ]`
                            : ''
                    }`}
                    defVal={
                        student.enrollment ? student.enrollment.paymentForm : ''
                    }
                    onChange={({ value }) =>
                        setEnrollmentPaymentForm(value as string)
                    }
                    activePFs={[
                        '',
                        constants.payment.paymentForms.purse.name,
                        constants.payment.paymentForms.cash.name,
                        constants.payment.paymentForms.bank.name,
                        cxt?.sc?.dataphoneConnected
                            ? constants.payment.paymentForms.dataphoneConnected
                                  .name
                            : constants.payment.paymentForms.dataphone.name,
                        constants.payment.paymentForms.receipt.name,
                        constants.payment.paymentForms.free.name,
                        cxt?.sc?.cashdroConnected === true &&
                            constants.payment.paymentForms.cashdro.name,
                    ]}
                />

                {/* --- Payment form - fee --- */}
                {selectedRate?.fee > 0 && (
                    <SttSelectPForm
                        grid
                        xs={12}
                        md={3}
                        name="feePaymentForm"
                        caption={`${t('generic.fee')}${
                            selectedRate
                                ? `[ ${formatPriceByLocale(
                                      selectedRate.fee,
                                      typeof selectedRate.feeShape === 'object'
                                          ? (selectedRate.feeShape?.currency ??
                                                'EUR')
                                          : 'EUR',
                                      typeof selectedRate.feeShape === 'object'
                                          ? (selectedRate.feeShape?.locale ??
                                                'es-ES')
                                          : 'es-ES',
                                  )} ]`
                                : ''
                        }`}
                        defVal={
                            selectedRate && selectedRate.fee !== 0
                                ? student.feePaymentForm
                                : ''
                        }
                        onChange={(value) => handleForm(value)}
                        activePFs={
                            selectedRate && selectedRate.fee !== 0
                                ? [
                                      '',
                                      constants.payment.paymentForms.receipt
                                          .name,
                                      cxt?.sc?.TPVFeePayment &&
                                          constants.payment.paymentForms.tpv
                                              .name,
                                  ]
                                : [constants.payment.paymentForms.free.name]
                        }
                    />
                )}

                {showDiscountsSelection && discounts && (
                    <Grid size={{ xs: 12 }}>
                        <Grid container>
                            {discounts.map((discount: DiscountForm) => (
                                <Grid key={discount.id} size={{ xs: 12 }}>
                                    <SttFormCheck
                                        name={discount.id.toString()}
                                        caption={`${discount.name} - ${discount.discountPercentage}%`}
                                        checked={discount.checked}
                                        onChange={(value) =>
                                            handleChangeDiscounts(value)
                                        }
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                )}

                {showDiscountsList && discounts && (
                    <Grid size={{ xs: 12 }}>
                        <DiscountList
                            discounts={discounts as Discount[]}
                            showEmptyMessage={false}
                        />
                    </Grid>
                )}

                {history && (
                    <Grid size={{ xs: 12 }}>
                        <Box pt={3}>
                            <SttLabelForm text={t('Historical')} />
                            <Box
                                style={{
                                    textAlign: 'center',
                                    border: '1px solid #cdcdcd',
                                }}
                                py={1}
                                mt={1}
                            >
                                <SttLabelForm text={t('Period')} />
                            </Box>
                            {history.map((item: any) => (
                                <Box
                                    key={item.id}
                                    display="flex"
                                    style={{
                                        border: '1px solid #cdcdcd',
                                        borderTop: 0,
                                    }}
                                >
                                    <Box
                                        p={1}
                                        flex={1}
                                        style={{ textAlign: 'center' }}
                                    >
                                        {format(
                                            parseISO(item.ini),
                                            'dd MMM yyyy, HH:mm',
                                            { locale: getDateLocale() },
                                        )}
                                    </Box>
                                    <Box
                                        p={1}
                                        flex={1}
                                        style={{
                                            textAlign: 'center',
                                            borderColor: '#cdcdcd',
                                            borderStyle: 'solid',
                                            borderWidth: 0,
                                            borderLeftWidth: 1,
                                        }}
                                    >
                                        {format(
                                            parseISO(item.end),
                                            'dd MMM yyyy, HH:mm',
                                            { locale: getDateLocale() },
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    </Grid>
                )}
            </>
        </Grid>
    );

    const tabFees = student.fees ? (
        <SttFeeList fees={student.fees} />
    ) : (
        <Typography>{t('generic.noFees')}</Typography>
    );

    const tabForm = student.form && (
        <Grid container spacing={3}>
            {student?.form?.map((item: any) => (
                <Grid key={item.id} size={{ xs: 12 }}>
                    {generateFormField(item, ({ name, value }) =>
                        handleFormFieldChange({ name, value }),
                    )}
                </Grid>
            ))}
        </Grid>
    );

    const availableTabs = [
        {
            caption: t('Student'),
            component: tabStudent,
        },
        {
            caption: t('generic.fees'),
            component: tabFees,
            show: !!student.id,
        },
        {
            caption: t('Form'),
            component: tabForm,
            show: !!student.form,
        },
    ];

    return (
        <>
            <SttDialog
                title={title}
                subtitle={
                    selectedRate?.activity
                        ? `${selectedRate.activity.name}, ${selectedRate.name}`
                        : ''
                }
                maxWidth="md"
                open={open}
                onClose={onClose}
                content={<SttTabs tabs={availableTabs} />}
                buttons={[
                    student.id && {
                        caption: `${t('generic.delete')} ${t(
                            'generic.inscription',
                        )}`,
                        icon: <KeyboardReturnIcon />,
                        onClick: () => deleteInscription(),
                    },
                    student.id && {
                        caption: t('ChangeClassAndRate'),
                        icon: <LoopIcon />,
                        onClick: () => setOpenChangeClassAndRateDialog(true),
                    },
                    !student.enrollment &&
                        selectedRate?.enrollment > 0 && {
                            caption: t('popup.student.generateSignup'),
                            onClick: () => {
                                generateStudentEnrollmentMutation.mutate({
                                    id: student.id,
                                });
                            },
                        },
                    {
                        type: 'save',
                        onClick: () => upsertStudent(),
                        permission: PERMISSIONS.ACTIVITY.MODIFY,
                        disabled: !student || (!student.id && !student.user),
                    },
                    student.id && {
                        type: 'unsubscribe',
                        onClick: () => setOpenAskingPaymentProcedure(true),
                    },
                ]}
                menu={[
                    {
                        caption: `${t('generic.download')} PDF`,
                        onClick: () => download(),
                    },
                ]}
            />

            <HandlePaymentsModal
                warningText={t(
                    'sc.bookings.delete.howToHandleActivityPayments',
                )}
                open={openAskingPaymentProcedure}
                onClose={() => setOpenAskingPaymentProcedure(false)}
                options={[
                    {
                        caption: t('associatedPayments.doNothing'),
                        value: '0',
                    },
                    {
                        caption: t('associatedPayments.delete'),
                        value: '1',
                    },
                    {
                        caption: t('associatedPayments.pending'),
                        value: '2',
                    },
                ]}
                onAccept={(option: string | null) => {
                    switch (option) {
                        case '0':
                            cancelActivity();
                            break; // PBM: 'do nothing' case was missing
                        case '1':
                            cancelActivity(1);
                            break;
                        case '2':
                            cancelActivity(2);
                            break;
                        default:
                    }
                }}
            />

            {/* Rate selector. Opened when user click in 'ChangeClassAndRate' button. */}
            <SttSelectClassAndRateDialog
                open={openChangeClassAndRateDialog}
                studentId={idStudent as number}
                onClose={() => {
                    setOpenChangeClassAndRateDialog(false);
                    onClose();
                }}
            />

            {cxt?.sc?.sporttiaStripeLocationId && posPaymentParams !== null ? (
                <POSStripePaymentDialog
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        saveAndClose();
                    }}
                />
            ) : (
                <DataphonePaymentDialog
                    open={posPaymentParams !== null}
                    paymentData={posPaymentParams}
                    onClose={() => setPosPaymentParams(null)}
                    onSuccess={() => {
                        saveAndClose();
                    }}
                />
            )}

            {/* CASHDRO payment dialog */}
            {cashdroPayment && (
                <CashdroControlDialog
                    open={cashdroPayment}
                    amount={cashdroPaymentAmount}
                    onClose={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        setPendingPaymentForm(null);
                    }}
                    onSuccess={() => {
                        setCashdroPayment(false);
                        setCashdroPaymentAmount(0);
                        let override: PaymentFormOverride = {};
                        if (
                            student.enrollment?.paymentForm ===
                            constants.payment.paymentForms.cashdro.name
                        ) {
                            override.enrollmentPaymentForm = '';
                        }
                        if (pendingPaymentForm) {
                            override = { ...pendingPaymentForm, ...override };
                        }
                        setPendingPaymentForm(null);
                        handleStudentSave(override);
                    }}
                />
            )}
        </>
    );
}
